/**
 * Merge an incoming WebSocket message into the current message list.
 *
 * Rules, in order:
 * 1. Drop the message if its message_id is already present (duplicate event).
 * 2. Outbound media echo: replace the local_media_* placeholder of the same
 *    attachment_type, keeping the placeholder's permanent S3 attachment_url.
 * 3. Outbound text echo: replace the oldest optimistic temp_* bubble with the
 *    same text (added on send, before the Instagram echo assigns a real mid).
 * 4. Otherwise append.
 *
 * Pure function — always returns prev untouched or a new array.
 *
 * @param {Array<object>} prev - current messages, oldest first.
 * @param {object} newMsg - normalized message from the new_message event.
 * @returns {Array<object>}
 */
export function upsertIncomingMessage(prev, newMsg) {
  if (prev.some((m) => m.message_id === newMsg.message_id)) return prev;

  if (newMsg.direction === "outbound" && newMsg.attachment_type) {
    const localIdx = prev.findIndex(
      (m) => m.message_id.startsWith("local_media_") && m.attachment_type === newMsg.attachment_type,
    );
    if (localIdx !== -1) {
      const updated = [...prev];
      updated[localIdx] = {
        ...newMsg,
        attachment_url: prev[localIdx].attachment_url || newMsg.attachment_url,
      };
      return updated;
    }
  }

  if (newMsg.direction === "outbound" && !newMsg.attachment_type) {
    const tempIdx = prev.findIndex(
      (m) => m.message_id.startsWith("temp_") && m.text === newMsg.text,
    );
    if (tempIdx !== -1) {
      const updated = [...prev];
      updated[tempIdx] = { ...newMsg };
      return updated;
    }
  }

  return [...prev, newMsg];
}
